import type { LayoutDocument } from '../layout/types'

/**
 * The pictures a printed ticket needs, as bytes the agent can be handed.
 *
 * The program that drives the printer talks to nothing but a printer and a port.
 * That is deliberate and worth keeping: it means a kiosk can be installed by
 * whoever plugs it in, with no server address, no credential and no certificate. So
 * a logo on a slip cannot be something the agent fetches — it has to be something
 * the page brings, and the page already has a connection to the server.
 *
 * Fetched once per design rather than once per ticket. A branch issuing four hundred
 * tickets a day would otherwise re-read the same logo four hundred times, and the
 * identifiers are content-addressed, so a design that has not changed cannot need a
 * picture that has.
 */
export async function picturesFor(
  document: LayoutDocument | null,
  urlFor: (assetId: string) => string,
): Promise<Record<string, string>> {
  if (!document) return {}

  const wanted = [
    ...new Set(
      document.elements.filter((el) => el.kind === 'image' && el.assetId).map((el) => el.assetId!),
    ),
  ]
  if (wanted.length === 0) return {}

  const out: Record<string, string> = {}
  await Promise.all(
    wanted.map(async (id) => {
      try {
        const response = await fetch(urlFor(id))
        if (!response.ok) return
        out[id] = await base64Of(await response.blob())
      } catch {
        // A picture that cannot be fetched is a gap on the slip, not a customer
        // without a number. The agent draws the rest and says so in its log.
      }
    }),
  )
  return out
}

/**
 * The pictures of several designs at once: the default slip and every service's.
 * One call to picturesFor over their elements together, so a logo that three slips
 * share is fetched once — the identifiers are content-addressed.
 */
export async function picturesForAll(
  documents: LayoutDocument[],
  urlFor: (assetId: string) => string,
): Promise<Record<string, string>> {
  if (documents.length === 0) return {}
  return picturesFor({ ...documents[0]!, elements: documents.flatMap((d) => d.elements) }, urlFor)
}

/** A blob as base64, which is how Go reads a []byte out of JSON. */
function base64Of(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error)
    reader.onload = () => {
      const url = String(reader.result)
      // "data:image/png;base64,AAAA…" — everything after the comma.
      const comma = url.indexOf(',')
      resolve(comma < 0 ? '' : url.slice(comma + 1))
    }
    reader.readAsDataURL(blob)
  })
}

/** The widest a slip picture is kept: 80 mm paper at 203 dpi. */
export const SLIP_PICTURE_MAX_SIDE = 576
/** The server's cap on a slip picture (layout.MaxTicketPictureBytes). */
export const SLIP_PICTURE_MAX_BYTES = 256 * 1024

/** Decoding and drawing, apart so a test can stand in for the browser's canvas. */
export interface PictureTools {
  decode(file: Blob): Promise<{ width: number; height: number; source: CanvasImageSource; close?: () => void }>
  encode(source: CanvasImageSource, width: number, height: number, type: 'image/png' | 'image/jpeg'): Promise<Blob>
}

const browserTools: PictureTools = {
  decode: async (file) => {
    const bitmap = await createImageBitmap(file)
    return { width: bitmap.width, height: bitmap.height, source: bitmap, close: () => bitmap.close() }
  },
  encode: (source, width, height, type) =>
    new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) return reject(new Error('pictures: no 2d canvas'))
      // JPEG has no transparency, and a transparent pixel would print black.
      if (type === 'image/jpeg') {
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, width, height)
      }
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(source, 0, 0, width, height)
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('pictures: encoding failed'))), type, 0.85)
    }),
}

// A slip picture shrunk to the paper's width and re-encoded before upload; one that
// cannot be decoded or drawn is returned as it is, for the server to judge.
export async function slipPicture(file: File, tools: PictureTools = browserTools): Promise<File> {
  let decoded: Awaited<ReturnType<PictureTools['decode']>>
  try {
    decoded = await tools.decode(file)
  } catch {
    return file
  }
  try {
    const scale = Math.min(1, SLIP_PICTURE_MAX_SIDE / Math.max(decoded.width, decoded.height))
    const width = Math.max(1, Math.round(decoded.width * scale))
    const height = Math.max(1, Math.round(decoded.height * scale))
    const plain = file.type === 'image/png' || file.type === 'image/jpeg'
    if (scale === 1 && plain && file.size <= SLIP_PICTURE_MAX_BYTES) return file

    let type: 'image/png' | 'image/jpeg' = file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png'
    let blob = await tools.encode(decoded.source, width, height, type)
    if (blob.size > SLIP_PICTURE_MAX_BYTES && type === 'image/png') {
      type = 'image/jpeg'
      blob = await tools.encode(decoded.source, width, height, type)
    }
    const stem = file.name.replace(/\.[^.]*$/, '') || 'picture'
    return new File([blob], `${stem}.${type === 'image/png' ? 'png' : 'jpg'}`, { type })
  } catch {
    return file
  } finally {
    decoded.close?.()
  }
}
